/**
 * A rename is ONE change, not "D old" + "A new" (card f93d5637, found in the
 * Windows Sandbox run of card 9269e283, 03.10.2026). Renaming a folder used to
 * list the old folder as deleted and the new one as added; discarding one half
 * left BOTH folders in the workspace until the other half was discarded too.
 *
 * Like git's own rename detection (and VS Code / GitHub Desktop showing "R"),
 * but exact: a deleted and an added item of the same group with the same
 * Apiboo id ARE the same item. A folder that moved on disk carries what is
 * inside it (its requests and subfolders moved with it), so those are part of
 * the folder's change — one row, one Discard that puts every file back.
 *
 * Pure: items from changeItems.describeChanges, files with their before/after text.
 */
import { parseYaml } from '../../utils/yamlWorkspace/deterministicYaml.js';

const PAIRED_GROUPS = new Set(['Requests', 'Folders']);

function idOf(text) {
  if (text == null) return null;
  try {
    const v = parseYaml(text);
    return v && typeof v === 'object' && v.id != null && v.id !== '' ? String(v.id) : null;
  } catch {
    return null;
  }
}

function parsed(text) {
  try {
    const v = text == null ? null : parseYaml(text);
    return v && typeof v === 'object' ? v : {};
  } catch {
    return {};
  }
}

const dirOf = (p) => p.slice(0, p.lastIndexOf('/'));

/**
 * @param {Array} items  ChangeItems (one per file for requests / nested folders)
 * @param {Map<string, {kind, before, after}>} filesByPath
 * @param {(before: object, after: object) => Array} [diff] field changes (changeItems.diffFields)
 * @returns {Array} items with renames merged: kind 'renamed', `fromPath`, `paths` (every file it covers), `previousName`
 */
export function pairRenames(items, filesByPath, diff = () => []) {
  const deleted = new Map();
  for (const it of items) {
    if (it.kind !== 'deleted' || !PAIRED_GROUPS.has(it.group)) continue;
    // Only a whole file that went away: items INSIDE a shared file (folders in
    // collection.yaml) already compare by id and never need pairing.
    const file = filesByPath.get(it.path);
    const id = file?.kind === 'deleted' ? idOf(file.before) : null;
    if (id) deleted.set(`${it.group}:${id}`, it);
  }
  if (!deleted.size) return items;

  const used = new Set();
  const renamed = new Map(); // added item → merged item
  for (const it of items) {
    if (it.kind !== 'added' || !PAIRED_GROUPS.has(it.group)) continue;
    const file = filesByPath.get(it.path);
    const id = file?.kind === 'added' ? idOf(file.after) : null;
    const old = id && deleted.get(`${it.group}:${id}`);
    if (!old || used.has(old)) continue;
    used.add(old);
    renamed.set(it, {
      ...it,
      kind: 'renamed',
      fromPath: old.path,
      paths: [old.path, it.path],
      previousName: old.name,
      fields: diff(parsed(filesByPath.get(old.path)?.before), parsed(filesByPath.get(it.path)?.after)),
    });
  }

  // What moved only because its folder moved belongs to the folder's change.
  // Outermost first: a request in a renamed subfolder of a renamed folder joins the outer one.
  const folders = [...renamed.values()]
    .filter((r) => r.group === 'Folders' && /\/folder\.yaml$/.test(r.path))
    .sort((a, b) => a.fromPath.length - b.fromPath.length);
  const inside = new Set();
  // (A subfolder inside a renamed folder is folded in the same way.)
  for (const r of renamed.values()) {
    const owner = folders.find(
      (f) => f !== r && r.fromPath.startsWith(`${dirOf(f.fromPath)}/`) && r.path.startsWith(`${dirOf(f.path)}/`)
        && r.fromPath.slice(dirOf(f.fromPath).length) === r.path.slice(dirOf(f.path).length)
    );
    if (owner) {
      owner.paths.push(...r.paths);
      owner.inside = (owner.inside || 0) + 1;
      // An edit made to something inside stays visible on the folder's row.
      owner.fields = [...owner.fields, ...r.fields.map((f) => ({ ...f, label: `${r.name} · ${f.label}` }))];
      inside.add(r);
    }
  }

  const out = [];
  for (const it of items) {
    if (used.has(it)) continue;
    const merged = renamed.get(it);
    if (!merged) out.push(it);
    else if (!inside.has(merged)) out.push(merged);
  }
  return out;
}

/**
 * What putting a change back writes, file by file: [{ path, content }]
 * (content null = the file goes). "discard" = as at the last sync; "restore"
 * = as that sync left it. A single-file item keeps versionFor's meaning.
 */
export function pairWrites(item, filesByPath, mode, versionFor) {
  if (!item.paths) return [{ path: item.path, content: versionFor(filesByPath.get(item.path), mode) }];
  return item.paths.map((path) => {
    const f = filesByPath.get(path);
    if (mode === 'discard') return { path, content: f?.kind === 'added' ? null : f?.before ?? null };
    return { path, content: f?.kind === 'deleted' ? null : f?.after ?? null };
  });
}

/** The file a row's raw diff shows: a rename compares the old file with the new one. */
export function pairFile(item, filesByPath) {
  if (!item.fromPath) return filesByPath.get(item.path);
  return { path: item.path, kind: 'modified', before: filesByPath.get(item.fromPath)?.before ?? null, after: filesByPath.get(item.path)?.after ?? null };
}
